import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtTRY, fmtDate } from "../lib/format";
import { PageHeader, StatusBadge } from "../components/Common";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash, PencilSimple, X, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = [
  { v: "active", l: "Aktif" },
  { v: "overdue", l: "Gecikmiş" },
  { v: "completed", l: "Tamamlandı" },
];

export default function Contracts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = () => api.get("/contracts").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/vehicles").then(({ data }) => setVehicles(data));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ vehicle_plate: vehicles[0]?.plate || "", customer_name: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", monthly_amount: "", status: "active" });
    setModal(true);
  };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, vehicle_id: form.vehicle_id || "", monthly_amount: parseFloat(form.monthly_amount) };
    try {
      if (editing) await api.put(`/contracts/${editing.id}`, payload);
      else await api.post("/contracts", payload);
      toast.success(editing ? "Sözleşme güncellendi" : "Sözleşme eklendi");
      setModal(false); load();
    } catch { toast.error("İşlem başarısız"); }
  };
  const remove = async (id) => { if (!window.confirm("Bu sözleşmeyi silmek istediğinize emin misiniz?")) return; await api.delete(`/contracts/${id}`); toast.success("Sözleşme silindi"); load(); };

  const downloadBulkPdf = async () => {
    try {
      const { data } = await api.get("/reports/contracts-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "aktif-sozlesmeler.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Aktif sözleşme bulunamadı" : "PDF indirilemedi");
    }
  };

  const downloadContractPdf = async (id, plate) => {
    try {
      const { data } = await api.get(`/contracts/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sozlesme-${(plate || "").replace(/\s/g, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Sözleşme PDF indirilemedi");
    }
  };

  const totalMonthly = items.filter(c => c.status === "active").reduce((s, c) => s + c.monthly_amount, 0);

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Sözleşmeler" subtitle="Kiralama sözleşmeleri ve müşteri takibi" action={
        <div className="flex items-center gap-3">
          <button onClick={downloadBulkPdf} data-testid="bulk-pdf-button" className="flex items-center gap-2 border border-zinc-200 bg-white rounded-sm px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><FilePdf size={16} weight="bold" className="text-[#002FA7]" /> Aktif Sözleşmeler PDF</button>
          <button onClick={openNew} data-testid="add-contract-button" className="bg-zinc-950 text-white rounded-sm px-4 py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors flex items-center gap-2">
            <Plus size={16} weight="bold" /> Sözleşme Ekle
          </button>
        </div>
      } />

      <div className="bg-white border border-zinc-200 rounded-sm mb-4 p-6 flex items-center justify-between animate-fade-up">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">Aktif Sözleşmeler Aylık Geliri</span>
        <span className="font-mono text-2xl font-semibold text-green-600 tabular-nums" data-testid="contracts-monthly-total">{fmtTRY(totalMonthly)}</span>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden animate-fade-up" data-testid="contracts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Müşteri</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Araç</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Başlangıç</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Bitiş</th>
              <th className="text-center font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Durum</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Aylık Tutar</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`contract-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-zinc-900">{c.customer_name}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{c.vehicle_plate}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(c.start_date)}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{fmtDate(c.end_date)}</td>
                <td className="px-4 py-3 text-center"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-zinc-900 tabular-nums">{fmtTRY(c.monthly_amount)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => downloadContractPdf(c.id, c.vehicle_plate)} data-testid={`pdf-contract-${c.id}`} className="p-1.5 text-zinc-400 hover:text-[#002FA7] transition-colors" title="Sözleşme PDF"><FilePdf size={16} /></button>
                    <button onClick={() => openEdit(c)} data-testid={`edit-contract-${c.id}`} className="p-1.5 text-zinc-400 hover:text-[#002FA7] transition-colors"><PencilSimple size={16} /></button>
                    {user?.role === "admin" && <button onClick={() => remove(c.id)} data-testid={`delete-contract-${c.id}`} className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"><Trash size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-sm border border-zinc-200 w-full max-w-md p-6 animate-fade-up" data-testid="contract-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-semibold">{editing ? "Sözleşmeyi Düzenle" : "Yeni Sözleşme"}</h3>
              <button type="button" onClick={() => setModal(false)}><X size={20} className="text-zinc-400 hover:text-zinc-900" /></button>
            </div>
            <div className="space-y-4">
              <F label="Müşteri"><input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="con-customer" className="inp" placeholder="Demir Lojistik A.Ş." /></F>
              <F label="Araç">
                <select value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value })} data-testid="con-vehicle" className="inp">
                  {vehicles.map((v) => <option key={v.id} value={v.plate}>{v.plate} — {v.brand} {v.model}</option>)}
                </select>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Başlangıç"><input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="con-start" className="inp" /></F>
                <F label="Bitiş"><input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="con-end" className="inp" /></F>
              </div>
              <F label="Aylık Tutar (₺)"><input type="number" required value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} data-testid="con-amount" className="inp font-mono" /></F>
              <F label="Durum">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="con-status" className="inp">
                  {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </F>
            </div>
            <button type="submit" data-testid="con-submit" className="w-full mt-6 bg-zinc-950 text-white rounded-sm py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors">Kaydet</button>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (<div><label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-1.5">{label}</label>{children}</div>);
}
